// Toast — minimal DS-native replacement for sonner.
//
// The design system ships a .toast CSS class and .toast-region container but no
// JS host. This module exposes a `toast()` function and a `<Toaster />` component
// that mounts a single fixed-position region and renders queued toasts via React
// state. A module-level event bus lets `toast()` push from anywhere without a
// hook/context — matching sonner's call-from-anywhere ergonomics.

import { useCallback, useEffect, useRef, useState } from 'react';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastOptions {
  tone?: ToastTone;
  title?: string;
  durationMs?: number;
  /** Defaults to true for danger/warning (must be dismissible for a11y). */
  dismissible?: boolean;
}

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  title?: string;
  durationMs: number;
  dismissible: boolean;
}

type Listener = (item: ToastItem) => void;
const listeners = new Set<Listener>();
let nextId = 1;

function emit(message: string, options: ToastOptions = {}): void {
  const tone = options.tone ?? 'info';
  const item: ToastItem = {
    id: nextId++,
    message,
    tone,
    durationMs: options.durationMs ?? 4000,
    dismissible: options.dismissible ?? (tone === 'danger' || tone === 'warning'),
    ...(options.title !== undefined ? { title: options.title } : {}),
  };
  for (const fn of listeners) fn(item);
}

// Public API. Plain call → info; tone helpers mirror sonner's `toast.success(...)`.
export const toast = Object.assign(
  (message: string, options?: ToastOptions) => emit(message, options),
  {
    info:    (m: string, o?: ToastOptions) => emit(m, { ...o, tone: 'info' }),
    success: (m: string, o?: ToastOptions) => emit(m, { ...o, tone: 'success' }),
    warning: (m: string, o?: ToastOptions) => emit(m, { ...o, tone: 'warning' }),
    error:   (m: string, o?: ToastOptions) => emit(m, { ...o, tone: 'danger' }),
  },
);

// Danger/warning toasts announce assertively; informational toasts stay polite.
function roleForTone(tone: ToastTone): 'alert' | 'status' {
  return tone === 'danger' || tone === 'warning' ? 'alert' : 'status';
}

function liveForTone(tone: ToastTone): 'assertive' | 'polite' {
  return tone === 'danger' || tone === 'warning' ? 'assertive' : 'polite';
}

export function Toaster(): React.JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);
  // Per-item timer ids keyed by toast id. Scheduling happens in onPush so that
  // pushing a new toast doesn't rebuild the existing timers (which previously
  // caused drift on rapid pushes).
  const timersRef = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  useEffect(() => {
    const onPush: Listener = (item) => {
      setItems((prev) => [...prev, item]);
      const timer = window.setTimeout(() => dismiss(item.id), item.durationMs);
      timersRef.current.set(item.id, timer);
    };
    listeners.add(onPush);
    const timers = timersRef.current;
    return () => {
      listeners.delete(onPush);
      for (const t of timers.values()) window.clearTimeout(t);
      timers.clear();
    };
  }, [dismiss]);

  return (
    <div className="toast-region" aria-label="Notifications">
      {items.map((it) => (
        <div
          key={it.id}
          className="toast"
          data-tone={it.tone}
          role={roleForTone(it.tone)}
          aria-live={liveForTone(it.tone)}
        >
          <div className="toast-body">
            {it.title && <div className="toast-title">{it.title}</div>}
            {it.message}
          </div>
          {it.dismissible && (
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              data-size="sm"
              data-icon-only
              aria-label="Dismiss notification"
              onClick={() => dismiss(it.id)}
              style={{ marginLeft: 'var(--space-2)' }}
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
